import { PAGE_DIMENSIONS, getContentHeight } from "./pageConstants";

const EPS = 3; // 오차 허용치 (px) - 서브픽셀 반올림 처리

/**
 * HTML 콘텐츠를 페이지 높이에 맞게 분할
 * @param {string} html - 변환된 HTML 콘텐츠
 * @param {string} pageSize - 'A4' 또는 'A5'
 * @param {number} coverHeight - 첫 페이지 커버 높이 (기본 150px)
 * @returns {string[]} 페이지별 HTML 배열
 */
export function splitContentByHeight(html, pageSize, coverHeight = 150, options = {}) {
  // 클라이언트 사이드에서만 실행
  if (typeof window === "undefined" || typeof document === "undefined") {
    return [html];
  }

  if (!html || html.trim() === "") {
    return [""];
  }

  const contentHeight = getContentHeight(pageSize);
  const pages = [];

  // 페이지 나누기(---) 및 references-section으로 먼저 분할
  const sections = html.split(
    /(?=<div class="[^"]*page-break-before[^"]*">)|<div class="page-break"><\/div>/
  );

  let isFirstSection = true;

  for (const section of sections) {
    if (!section.trim()) {
      continue;
    }

    // 각 섹션을 높이에 맞게 분할
  const sectionPages = splitSectionByHeight(
      section,
      contentHeight,
      pageSize,
      isFirstSection ? coverHeight : 0,
      options
    );
    pages.push(...sectionPages);
    isFirstSection = false;
  }

  return pages.length > 0 ? pages : [""];
}

/**
 * 단일 섹션을 높이 기반으로 분할
 */
function splitSectionByHeight(html, maxHeight, pageSize, initialCoverHeight, options) {
  // 임시 컨테이너 생성 (실제 렌더 스타일과 동일하게 맞춤)
  const container = document.createElement("div");
  const {
    containerClassName = "paper-content",
    containerStyle = "",
    paragraphStyle = "",
  } = options || {};
  container.className = containerClassName;
  container.innerHTML = html;

  const dim = PAGE_DIMENSIONS[pageSize];
  const contentWidth =
    pageSize === "A5"
      ? dim.width - dim.bindingInner - dim.bindingOuter
      : dim.width - dim.horizontalPadding * 2;

  container.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: ${contentWidth}px;
    ${containerStyle}
  `;
  document.body.appendChild(container);
  if (paragraphStyle) {
    container.querySelectorAll("p").forEach((p) => {
      p.style.cssText = `${p.style.cssText}; ${paragraphStyle}`.trim();
    });
  }

  const pages = [];
  let currentPageContent = [];
  let currentHeight = 0;

  // 첫 페이지는 커버 높이 고려
  let availableHeight = maxHeight - initialCoverHeight;

  const children = Array.from(container.children);

  for (const element of children) {
    const elementHeight = element.offsetHeight;

    if (currentHeight + elementHeight <= availableHeight + EPS) {
      // 현재 페이지에 추가
      currentPageContent.push(element.outerHTML);
      currentHeight += elementHeight;
    } else {
      // 새 페이지로
      if (currentPageContent.length > 0) {
        pages.push(currentPageContent.join("\n"));
      }
      currentPageContent = [element.outerHTML];
      currentHeight = elementHeight;
      availableHeight = maxHeight; // 이후 페이지는 커버 없음
    }
  }

  // 마지막 페이지
  if (currentPageContent.length > 0) {
    pages.push(currentPageContent.join("\n"));
  }

  document.body.removeChild(container);

  return pages.length > 0 ? pages : [""];
}

/**
 * 분할 불가 요소 체크 (참고용)
 */
export function isNonBreakableElement(element) {
  const tagName = element.tagName?.toLowerCase() || "";
  const className = element.className || "";

  return (
    ["h1", "h2", "h3", "h4", "h5", "h6", "table", "figure", "img"].includes(
      tagName
    ) ||
    className.includes("blockquote") ||
    className.includes("reference-item") ||
    className.includes("table-figure")
  );
}
